export type ResponseType = "success" | "error" | "warning";

export interface ResponseResult {
  type: ResponseType;
  message: string;
}

export interface ApiResponseRow {
  ResponseID: number;
  StatusCode: "A" | "F" | "R" | "W";
  Status: "Accepted" | "Failure" | "Rejected" | "Warning";
  Message: string;
  Data: any;
}

export interface ApiResponse {
  ok: boolean;
  data?: {
    tables?: Array<{
      rows?: ApiResponseRow[];
    }>;
  };
  message?: string;
}

/**
 * Centralized handler for API responses with catResponseStatus codes
 * Maps status codes to appropriate UI feedback types
 */
export const handleApiResponse = (response: ApiResponse): ResponseResult => {
  // Handle network/API level errors first
  if (!response.ok) {
    return {
      type: "error",
      message: response.message || "Operation failed",
    };
  }

  // Extract response data
  const tables = response.data?.tables;
  if (
    !tables ||
    tables.length === 0 ||
    !tables[0].rows ||
    tables[0].rows.length === 0
  ) {
    return {
      type: "success",
      message: "Operation completed successfully",
    };
  }

  const firstRow = tables[0].rows[0];
  const { StatusCode, Message } = firstRow;

  // Map catResponseStatus codes to UI feedback
  switch (StatusCode) {
    case "A": // Accepted
      return {
        type: "success",
        message: Message || "Operation completed successfully",
      };

    case "F": // Failure
    case "R": // Rejected
      return {
        type: "error",
        message: Message || "Operation failed",
      };

    case "W": // Warning
      return {
        type: "warning",
        message: Message || "Operation completed with warnings",
      };

    default:
      return {
        type: "error",
        message: `Unknown response status: ${StatusCode}`,
      };
  }
};

/**
 * Utility to get alert severity for Material-UI Alert component
 */
export const getAlertSeverity = (
  type: ResponseType,
): "success" | "error" | "warning" | "info" => {
  switch (type) {
    case "success":
      return "success";
    case "error":
      return "error";
    case "warning":
      return "warning";
    default:
      return "info";
  }
};
